import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  FaUserGraduate,
  FaClipboardList,
  FaCalendarCheck,
  FaClock,
  FaUsers,
  FaChartLine,
  FaExclamationTriangle
} from "react-icons/fa";
import { FiTrendingUp, FiTrendingDown, FiMoreVertical } from "react-icons/fi";
import useApi from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import SchoolProfileProgress from "../SchoolProfileProgress";

import "./dashboard.css";

const GrowthChart = ({ data = [], color = "#3699FF" }) => {
  const height = 100;
  const padding = 20;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;

  const points = data.map((val, i) => {
    const x = (i / (data.length - 1 || 1)) * 100;
    const y = height - padding - ((val - min) / range) * (height - padding * 2);
    return `${x}% ${y}`;
  });

  const areaPath = `M 0 ${height} L ${points.join(" L ")} L 100% ${height} Z`;

  return (
    <div className="growth-chart-container" style={{ height: `${height}px` }}>
      <svg width="100%" height="100%" preserveAspectRatio="none">
        <path d={areaPath} fill={`${color}15`} stroke="none" />
        <path
          d={`M ${points.join(" L ")}`}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {data.map((_, i) => {
          const x = (i / (data.length - 1 || 1)) * 100;
          const y =
            height -
            padding -
            ((data[i] - min) / range) * (height - padding * 2);
          return (
            <circle
              key={i}
              cx={`${x}%`}
              cy={y}
              r="3.5"
              fill={color}
              stroke="white"
              strokeWidth="1.5"
            />
          );
        })}
      </svg>

      <div className="chart-labels d-flex justify-content-between mt-2 text-muted fs-7">
        {data.map((val, i) => (
          <div key={i} className="text-center" style={{ flex: 1 }}>
            {val}
          </div>
        ))}
      </div>
    </div>
  );
};

const StatsCard = ({ title, value, icon, color, trend, details }) => {
  const icons = {
    students: <FaUserGraduate />,
    enrollments: <FaClipboardList />,
    sessions: <FaCalendarCheck />,
    calendar: <FaClock />
  };

  const trendColor = trend >= 0 ? "success" : "danger";
  const TrendIcon = trend >= 0 ? FiTrendingUp : FiTrendingDown;

  return (
    <div className={`card shadow-sm border-0 h-100 kt-stats-card ${color}`}>
      <div className="card-body">
        <div className="d-flex align-items-center mb-3">
          <div className={`icon-wrapper bg-${color} bg-opacity-10 text-${color}`}>
            {icons[icon] || <FaChartLine />}
          </div>
          <div className="ms-3 min-width-0">
            <h6 className="card-title mb-0 text-muted text-truncate">{title}</h6>
            <h3 className="mb-0 mt-1 fw-bold">
              {value?.toLocaleString() || "0"}
            </h3>
          </div>
        </div>

        {trend !== undefined && (
          <div className={`badge bg-light-${trendColor} text-${trendColor} px-2 py-1 fs-7 mb-2`}>
            <TrendIcon className="me-1" />
            {Math.abs(trend)}% {trend >= 0 ? "up" : "down"}
          </div>
        )}

        {details && <div className="small text-muted mt-2">{details}</div>}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [enrollmentTrend, setEnrollmentTrend] = useState([]);
  const [docData, setDocData] = useState(null);
  const [workingHours, setWorkingHours] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const [hoursConfigured, setHoursConfigured] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState("");
  const { bearerAuth } = useApi();
  const { user } = useAuth();

  const buildEnrollmentTrend = (recent, total) => {
    const days = Array(7).fill(0);
    const today = new Date();
    (recent || []).forEach((en) => {
      if (!en?.start_date) return;
      const d = new Date(en.start_date);
      if (Number.isNaN(d.getTime())) return;
      const diff = Math.floor((today - d) / 86400000);
      if (diff >= 0 && diff < 7) {
        days[6 - diff] += 1;
      }
    });
    if (days.every((v) => v === 0)) {
      const base = Number(total || 0);
      const avg = base ? Math.max(1, Math.round(base / 7)) : 0;
      return Array(7).fill(avg);
    }
    return days;
  };

  useEffect(() => {
    setLoading(true);
    setError("");
    bearerAuth("driving-school/dashboard")
      .then((res) => {
        const dashboard = res?.data?.data || {};
        setData(dashboard);

        const recent = dashboard.recent_enrollments || [];
        const trend = buildEnrollmentTrend(recent, dashboard.stats?.total_enrollments || 0);
        setEnrollmentTrend(trend);
      })
      .catch((err) => {
        console.error("Dashboard error:", err);
        setError("Failed to load dashboard data.");
        setData(null);
        setEnrollmentTrend([0, 0, 0, 0, 0, 0, 0]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setProfileLoading(true);
    Promise.all([
      bearerAuth("driving-school/documents", "GET"),
      bearerAuth("driving-school/working-hours", "GET")
    ])
      .then(([docRes, hoursRes]) => {
        setDocData(docRes?.data?.data || null);
        const hours = hoursRes?.data?.data || [];
        setWorkingHours(hours);
        setHoursConfigured(Array.isArray(hours) && hours.some((h) => h.status === "open"));
      })
      .catch(() => {
        setDocData(null);
        setWorkingHours([]);
        setHoursConfigured(false);
      })
      .finally(() => setProfileLoading(false));
  }, []);

  useEffect(() => {
    if (!data) return;
    const todayKey = new Date().toISOString().slice(0, 10);
    const calendarDays = data?.class_calendar?.days || [];
    const hasToday = calendarDays.some((day) => day?.date === todayKey);
    if (hasToday) {
      setSelectedCalendarDate(todayKey);
      return;
    }
    setSelectedCalendarDate(calendarDays[0]?.date || todayKey);
  }, [data]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger d-flex align-items-center justify-content-between mt-4">
        <div>
          <FaExclamationTriangle className="me-2" />
          {error}
        </div>
        <button className="btn btn-outline-danger btn-sm" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  const isEmpty = data?.stats?.total_enrollments === 0;
  const reminders = data?.daily_reminders || {};
  const todayStarting = reminders.today_starting_list || [];
  const newToday = reminders.new_today_list || [];
  const classCalendar = data?.class_calendar || {};
  const calendarMonthKey = classCalendar.month || new Date().toISOString().slice(0, 7);
  const [calendarYearRaw, calendarMonthRaw] = calendarMonthKey.split("-");
  const calendarYear = Number(calendarYearRaw) || new Date().getFullYear();
  const calendarMonth = Number(calendarMonthRaw) || new Date().getMonth() + 1;
  const calendarMonthStart = new Date(calendarYear, calendarMonth - 1, 1);
  const calendarDaysInMonth = new Date(calendarYear, calendarMonth, 0).getDate();
  const calendarFirstWeekday = calendarMonthStart.getDay();
  const calendarCells = [
    ...Array(calendarFirstWeekday).fill(null),
    ...Array.from({ length: calendarDaysInMonth }, (_, index) => index + 1)
  ];
  const calendarMap = (classCalendar.days || []).reduce((acc, day) => {
    if (day?.date) acc[day.date] = day;
    return acc;
  }, {});
  const selectedDayClasses = calendarMap[selectedCalendarDate]?.items || [];
  const profileUser = { ...(user || {}), ...(data?.school || {}) };

  const formatDateOnly = (value) => {
    if (!value) return "-";
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  };

  const badgeClassForStatus = (raw) => {
    const status = String(raw || "").toLowerCase();
    if (status === "accepted" || status === "in_progress") return "bg-success";
    if (status === "scheduled") return "bg-info text-dark";
    if (status === "pending") return "bg-warning text-dark";
    if (status === "rejected") return "bg-danger";
    if (status === "completed") return "bg-primary";
    return "bg-secondary";
  };

  return (
    <div className="container-fluid py-3 py-md-4 px-2 px-md-4 dashboard-wrapper">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mb-md-4 gap-3">
        <div className="w-100">
          <h2 className="fw-bold mb-1 fs-4 fs-md-2">Welcome {data?.school?.name || data?.school?.username || "Admin"}</h2>
          <div className="text-muted small">Last updated: {data?.last_updated || "-"}</div>
        </div>
      </div>

      <div className="mb-3 mb-md-4">
        {profileLoading ? (
          <div className="card shadow-sm border-0">
            <div className="card-body text-muted">Loading profile completion...</div>
          </div>
        ) : (
          <SchoolProfileProgress
            user={profileUser}
            docData={docData}
            workingHours={workingHours}
            hoursConfigured={hoursConfigured}
          />
        )}
      </div>

      <div className="row g-3 g-md-4 mb-4 mb-md-5">
        {data?.cards?.map((card, i) => (
          <div className="col-12 col-sm-6 col-lg-3" key={i}>
            <StatsCard
              title={card.title}
              value={card.value}
              icon={card.icon}
              color={card.color}
              details={
                card.icon === "students" ? (
                  <span className="d-block small">
                    Created: <strong>{data.stats?.created_by_you || 0}</strong> - Assigned:{" "}
                    <strong>{data.stats?.assigned_to_you || 0}</strong>
                  </span>
                ) : null
              }
            />
          </div>
        ))}
      </div>

      <div className="card shadow-sm border-0 mb-4 mb-md-5">
        <div className="card-header bg-white border-0 py-3">
          <h5 className="mb-1">Today Class Reminders</h5>
          <small className="text-muted">Track ongoing, starting today, and fresh enrollments at one place</small>
        </div>
        <div className="card-body">
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-4">
              <div className="border rounded p-3 h-100">
                <div className="text-muted small">Ongoing Enrollments</div>
                <div className="fw-bold fs-3">{reminders.ongoing_enrollments || 0}</div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="border rounded p-3 h-100">
                <div className="text-muted small">Starting Today</div>
                <div className="fw-bold fs-3">{reminders.starting_today_enrollments || 0}</div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="border rounded p-3 h-100">
                <div className="text-muted small">New Enrollments Today</div>
                <div className="fw-bold fs-3">{reminders.new_enrollments_today || 0}</div>
              </div>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-12 col-lg-6">
              <h6 className="mb-2">Classes Starting Today</h6>
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th className="d-none d-md-table-cell">Slot</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {todayStarting.length ? todayStarting.map((row) => (
                      <tr key={`start-${row.id}`}>
                        <td className="text-truncate" style={{ maxWidth: '100px' }}>{row.student || "-"}</td>
                        <td className="text-truncate" style={{ maxWidth: '100px' }}>{row.course || "-"}</td>
                        <td className="d-none d-md-table-cell">{row.slot || "-"}</td>
                        <td>
                          <span className={`badge ${badgeClassForStatus(row.status)}`}>
                            {row.status || "-"}
                          </span>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={4} className="text-muted">No classes starting today.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="col-12 col-lg-6">
              <h6 className="mb-2">New Enrollments Today</h6>
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th className="d-none d-md-table-cell">Created</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newToday.length ? newToday.map((row) => (
                      <tr key={`new-${row.id}`}>
                        <td className="text-truncate" style={{ maxWidth: '100px' }}>{row.student || "-"}</td>
                        <td className="text-truncate" style={{ maxWidth: '100px' }}>{row.course || "-"}</td>
                        <td className="d-none d-md-table-cell">{row.created_at ? new Date(row.created_at).toLocaleString() : "-"}</td>
                        <td>
                          <span className={`badge ${badgeClassForStatus(row.status)}`}>
                            {row.status || "-"}
                          </span>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={4} className="text-muted">No new enrollments today.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm border-0 mb-4 mb-md-5">
        <div className="card-header bg-white border-0 py-3">
          <div>
            <h5 className="mb-1">Class Calendar</h5>
            <small className="text-muted">Check classes date-wise for {calendarMonthStart.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</small>
          </div>
        </div>
        <div className="card-body">
          <div className="row g-4">
            <div className="col-12 col-lg-5">
              <div className="class-calendar-weekdays">
                {["S", "M", "T", "W", "T", "F", "S"].map((label, i) => (
                  <div key={i} className="class-calendar-weekday" title={["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][i]}>
                    {label}
                  </div>
                ))}
              </div>
              <div className="class-calendar-grid">
                {calendarCells.map((day, index) => {
                  if (!day) {
                    return <div key={`empty-${index}`} className="class-calendar-cell empty" />;
                  }

                  const dateKey = `${calendarMonthKey}-${String(day).padStart(2, "0")}`;
                  const dayData = calendarMap[dateKey];
                  const isSelected = selectedCalendarDate === dateKey;

                  return (
                    <button
                      key={dateKey}
                      type="button"
                      className={`class-calendar-cell ${isSelected ? "selected" : ""} ${dayData?.total ? "has-classes" : ""}`}
                      onClick={() => setSelectedCalendarDate(dateKey)}
                    >
                      <span className="day">{day}</span>
                      {dayData?.total ? <span className="count">{dayData.total}</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="col-12 col-lg-7">
              <h6 className="mb-2">Classes on {formatDateOnly(selectedCalendarDate)}</h6>
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th className="d-none d-md-table-cell">Slot</th>
                      <th className="d-none d-md-table-cell">Course Day</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDayClasses.length ? selectedDayClasses.map((row) => (
                      <tr key={`calendar-${row.id}`}>
                        <td className="text-truncate" style={{ maxWidth: '80px' }}>{row.student || "-"}</td>
                        <td className="text-truncate" style={{ maxWidth: '80px' }}>{row.course || "-"}</td>
                        <td className="d-none d-md-table-cell">{row.slot || "-"}</td>
                        <td className="d-none d-md-table-cell">
                          {row.course_day && row.course_total_days
                            ? `${row.course_day}/${row.course_total_days}`
                            : "-"}
                        </td>
                        <td>
                          <span className={`badge ${badgeClassForStatus(row.status)}`}>
                            {row.status || "-"}
                          </span>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={5} className="text-muted">No classes on this date.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 g-md-4 mb-4 mb-md-5">
        <div className="col-12 col-lg-8">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white border-0 py-3">
              <div>
                <h5 className="mb-1">Enrollment Growth</h5>
                <small className="text-muted">Last 7 days</small>
              </div>
            </div>
            <div className="card-body pt-2">
              {isEmpty ? (
                <div className="empty-chart-state text-center py-5">
                  <FaChartLine size={48} className="text-muted mb-3 opacity-50" />
                  <h6 className="text-muted">No enrollment data yet</h6>
                  <small className="text-muted">New enrollments will appear here</small>
                </div>
              ) : (
                <GrowthChart data={enrollmentTrend} color="#3699FF" />
              )}
              <div className="text-end mt-2">
                <span className="fw-bold">
                  Total: {data?.stats?.total_enrollments?.toLocaleString() || "0"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div className="row g-3 g-md-4">
            <div className="col-6 col-lg-12">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Expected Revenue</h6>
                  <h3 className="fw-bold mb-1">-</h3>
                  <small className="text-muted">Not available</small>
                </div>
              </div>
            </div>
            <div className="col-6 col-lg-12">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Pending Enrollments</h6>
                  <h3 className="fw-bold mb-1 text-warning">
                    {data?.stats?.pending_enrollments || 0}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm border-0 mb-4 mb-md-5">
        <div className="card-header bg-white border-0 py-3">
          <div>
            <h5 className="mb-1">Recent Enrollments</h5>
            <small className="text-muted">Latest student registrations</small>
          </div>
        </div>
        <div className="card-body p-0">
          {data?.recent_enrollments?.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Student</th>
                    <th>Course</th>
                    <th>Status</th>
                    <th className="d-none d-md-table-cell">Start Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_enrollments.map((en, i) => (
                    <tr key={i}>
                      <td className="text-truncate" style={{ maxWidth: '100px' }}>{en.student || "-"}</td>
                      <td className="text-truncate" style={{ maxWidth: '100px' }}>{en.course || "-"}</td>
                      <td>
                        <span className={`badge bg-${en.status === "Accepted" ? "success" : "warning"}`}>
                          {en.status || "Unknown"}
                        </span>
                      </td>
                      <td className="d-none d-md-table-cell">{en.start_date || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
              <FaExclamationTriangle size={36} className="mb-3 opacity-50" />
              <p className="mb-1 fw-medium">No recent enrollments</p>
              <small>New registrations will appear here</small>
            </div>
          )}
        </div>
      </div>

      <div className="row g-3">
        <div className="col-6 col-md-6">
          <div className="card shadow-sm border-0">
            <div className="card-body d-flex align-items-center p-3 p-md-4">
              <div className="icon-circle bg-primary bg-opacity-10 text-primary me-3">
                <FaUsers size={20} />
              </div>
              <div className="min-width-0">
                <h6 className="mb-1 text-muted small">Total Students</h6>
                <h4 className="mb-0 fw-bold fs-5">{data?.stats?.students || 0}</h4>
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-6">
          <div className="card shadow-sm border-0">
            <div className="card-body d-flex align-items-center p-3 p-md-4">
              <div className="icon-circle bg-success bg-opacity-10 text-success me-3">
                <FaClipboardList size={20} />
              </div>
              <div className="min-width-0">
                <h6 className="mb-1 text-muted small">Total Enrollments</h6>
                <h4 className="mb-0 fw-bold fs-5">{data?.stats?.total_enrollments || 0}</h4>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;